import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, Button, UncontrolledTooltip } from 'reactstrap';
import { Loader, CheckCircle, XCircle } from 'react-feather';
import { Instancia } from '../helpers/axios';
import { PASARELAS, labelPasarela } from '../../../helpers/pasarelas';
import logger from '../../../services/logger/logger';

export const ModalApi = ({ header, raw, deployment, mp, usesD2L = true }) => {
  const [modalState, setModalState] = useState(true);
  const [numMatricula, setNumMatricula] = useState(null);

  logger.debug('ModalApi usesD2L:', usesD2L, typeof usesD2L);

  // Determinar el endpoint del campus según la flag usesD2L
  const campusEndpoint = usesD2L ? 'crearCuentaD2L' : 'crearCuentaMoodle';
  logger.debug('ModalApi endpoint campus:', campusEndpoint);
  const campusLabel = usesD2L
    ? 'Activando cuenta del campus (D2L)'
    : 'Activando cuenta del campus (Additio)';

  const endpoints = [
    {
      id: 1,
      url: 'guardarMatricula',
      label: 'Guardando matrícula',
      method: 'POST',
    },
    {
      id: 2,
      url: 'crearCuentaGoogle',
      label: 'Activando cuenta de Google',
      method: 'POST',
    },
    {
      id: 3,
      url: campusEndpoint,
      label: campusLabel,
      method: 'POST',
    },
    {
      id: 4,
      url: 'crearCuentaOffice',
      label: 'Activando cuenta de Office 365',
      method: 'POST',
    },
  ];

  if (mp === PASARELAS.STRIPE) {
    endpoints.push({
      id: 5,
      url: 'crearCuentaStripe',
      label: `Activando cuenta de ${labelPasarela(mp)}`,
      method: 'POST',
    });
  } else if (mp === PASARELAS.REDSYS) {
    // Redsys: 1) crear recibos con id_fp=7, 2) enviar email al alumno con
    // el link al portal (uniqueid). Si el paso 1 falla, el 2 no se ejecuta.
    endpoints.push({
      id: 5,
      url: 'crearCuentaRedsys',
      label: 'Creando recibos',
      method: 'POST',
    });
    endpoints.push({
      id: 6,
      url: 'enviarEmailPagoRedsys',
      label: `Enviando email de pago ${labelPasarela(mp)}`,
      method: 'POST',
    });
  }

  const [statuses, setStatuses] = useState(() =>
    endpoints.reduce((acc, endpoint) => {
      acc[endpoint.id] = { status: 'loading', error: '' };
      return acc;
    }, {}),
  );

  useEffect(() => {
    const fetchData = async () => {
      // Verifica si raw es string y, en tal caso, lo parsea.
      const parsedRaw = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const instance = Instancia(deployment);
      let currentNumMatricula = null;
      let redsysCreacionOk = true;

      for (const endpoint of endpoints) {
        // Si crearCuentaRedsys falló, no llamamos al email: marcamos como
        // error explícito y pasamos al siguiente endpoint.
        const skipPorRedsysFallido =
          endpoint.url === 'enviarEmailPagoRedsys' && !redsysCreacionOk;

        if (skipPorRedsysFallido) {
          setStatuses((prev) => ({
            ...prev,
            [endpoint.id]: {
              status: 'error',
              error: 'No se envía el email porque crearCuentaRedsys falló.',
            },
          }));
        } else {
          try {
          // Construye el body según el endpoint, usando parsedRaw
          let body;
          if (
            endpoint.url === 'crearCuentaOffice' ||
            endpoint.url === 'crearCuentaGoogle' ||
            endpoint.url === 'crearCuentaD2L' ||
            endpoint.url === 'crearCuentaMoodle'
          ) {
            body = {
              idDelegacion: parsedRaw.idDelegacion,
              empresaOrigen: parsedRaw.empresaOrigen,
              origen: parsedRaw.origen,
              ccodcli: parsedRaw.ccodcli,
            };
          } else if (endpoint.url === 'enviarEmailPagoRedsys') {
            body = {
              numMatricula: currentNumMatricula,
              headquarter: parsedRaw.headquarter,
              ccodcli: parsedRaw.ccodcli,
              userEmail: parsedRaw.userEmail,
            };
          } else {
            // guardarMatricula, crearCuentaStripe, crearCuentaRedsys → raw completo
            body = parsedRaw;
          }

          const response =
            endpoint.method === 'POST'
              ? await instance.post(endpoint.url, body)
              : await instance.get(endpoint.url);

          // Tras guardar la matrícula, capturamos el número devuelto por el
          // backend para usarlo en los endpoints posteriores (p.ej. Redsys).
          if (endpoint.url === 'guardarMatricula') {
            const data = response?.data;
            currentNumMatricula =
              data?.numMatricula ||
              data?.NUM_Matricula ||
              data?.Num_Matricula ||
              data?.id ||
              data?.idMatricula ||
              null;
            if (currentNumMatricula) setNumMatricula(currentNumMatricula);
          }

          setStatuses((prev) => ({
            ...prev,
            [endpoint.id]: {
              status: response.status === 200 ? 'success' : 'error',
              error: response.status !== 200 ? response.error || 'Error desconocido' : '',
            },
          }));
          } catch (error) {
            if (endpoint.url === 'crearCuentaRedsys') redsysCreacionOk = false;
            setStatuses((prev) => ({
              ...prev,
              [endpoint.id]: {
                status: 'error',
                error: error?.response?.data?.message || error?.message || 'Error inesperado',
              },
            }));
          }
        }
      }
    };

    fetchData();
  }, [deployment, raw]);

  const allProcessesFinished = Object.values(statuses).every((item) => item.status !== 'loading');

  const handleClose = () => {
    setModalState(false);
    // El headquarter debe ser el de la matrícula RECIÉN creada (viaja en `raw`
    // como `headquarter`), no el de localStorage 'enrollment', que corresponde a
    // la última matrícula abierta en Revisión y puede ser de otra sede. Como el
    // número de matrícula existe en ambas sedes, usar el obsoleto abría la sede
    // equivocada (crear en CNTEC -> abría CEDESCA con el mismo número).
    let headquarter = null;
    try {
      headquarter = JSON.parse(raw)?.headquarter;
    } catch {
      headquarter = null;
    }
    if (headquarter && numMatricula) {
      window.location.href = `/solicitudes-matricula/${String(
        headquarter,
      ).toLowerCase()}/${numMatricula}/revision`;
    } else {
      window.location.href = `/solicitudes-matricula`;
    }
  };

  return (
    <Modal isOpen={modalState} centered>
      <ModalBody>
        <h4 className="text-center mt-3">{header}</h4>
        <ul>
          {endpoints.map(({ id, label }) => (
            <li key={id} className="d-flex align-items-center">
              {statuses[id].status === 'loading' && <Loader className="text-primary me-2" />}
              {statuses[id].status === 'success' && <CheckCircle className="text-success me-2" />}
              {statuses[id].status === 'error' && (
                <>
                  <XCircle className="text-danger me-2" id={`tooltip-${id}`} />
                  <UncontrolledTooltip placement="right" target={`tooltip-${id}`}>
                    {statuses[id].error}
                  </UncontrolledTooltip>
                </>
              )}
              {label}
            </li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleClose} disabled={!allProcessesFinished}>
          Continuar
        </Button>
      </ModalFooter>
    </Modal>
  );
};
